<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useSubscriptionFormStore } from "@modules/abonnement/stores/subscription-form.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Offre et engagement",
            commitment: "Engagement 12 mois",
            without_commitment: "Sans engagement",
            commitment_per: "Jusqu'à {per} d'économie",
            commitment_savings_pill:
                "Engagement 12 mois = jusqu'à {per} d'économie",
            badge_most_chosen: "Le plus choisi",
            modules_title: "Modules optionnels",
            modules_help:
                "Cliquez sur les modules pour les ajouter ou les retirer de votre offre.",
            previous: "Précédent",
            next: "Continuer",
            error_offers_load:
                "Impossible de charger les offres. Veuillez réessayer ou contacter le support.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const subscriptionFormStore = useSubscriptionFormStore()
const {
    getOffersFormatted,
    getModulesFormatted,
    getMaxDiscountPercent,
    offerSelected,
    moduleSelected,
    commitment,
    hasErrorStripeOffers,
    errorStripeOffers,
} = storeToRefs(subscriptionFormStore)
const { setCommitment, setOfferSelected, setModuleSelected } =
    subscriptionFormStore

// ================================
// PROPS & EMITS
// ================================
defineProps<{
    /** True pendant la mise à jour du deal Pipedrive (bouton « Continuer » en chargement). */
    nextLoading?: boolean
}>()

const emit = defineEmits<{
    (e: "back"): void
    (e: "next"): void
}>()

// ================================
// BUSINESS LOGIC
// ================================
/** Formate un pourcentage entier. */
function formatPercent(value: number): string {
    return `${Math.round(value)} %`
}

// ================================
// COMPUTED
// ================================
/** Titre optionnel engagement (économie en %). */
const getCommitmentExtraTitle = computed(() =>
    t("commitment_per", {
        per: formatPercent(getMaxDiscountPercent.value),
    })
)

/** Badge "Le plus choisi" pour l'offre EM. */
const OFFER_NAME_MOST_CHOSEN = "EM"

/** Pastille économie engagement (affichée sur le prix quand engagement 12 mois). */
const getCommitmentSavingsPill = computed(() =>
    commitment.value && getMaxDiscountPercent.value > 0
        ? t("commitment_savings_pill", {
              per: formatPercent(getMaxDiscountPercent.value),
          })
        : undefined
)

/** Message d'erreur affiché si le chargement des offres a échoué. */
const getOffersErrorMessage = computed(() => {
    const err = errorStripeOffers.value
    return err?.message ?? t("error_offers_load")
})

// ================================
// INTERACTIONS
// ================================
/** Met à jour la sélection des modules optionnels. */
function onModuleSelected(payload: { name: string; selected: boolean }): void {
    setModuleSelected(payload)
}
</script>

<template>
    <BaseCard
        class="subscription-form-offer"
        :pt="{
            header: 'mx-auto p-6',
            content: 'flex flex-col items-center gap-5',
        }"
    >
        <template #header>
            <h1
                class="text-xl font-semibold text-primary-900 text-center flex items-center gap-2"
            >
                {{ t("title") }}
            </h1>
        </template>
        <template #content>
            <BaseMessage
                v-if="hasErrorStripeOffers"
                severity="error"
                class="w-full"
            >
                {{ getOffersErrorMessage }}
            </BaseMessage>
            <div class="flex flex-col sm:flex-row gap-3 w-full justify-center">
                <BaseButton
                    :severity="commitment ? 'primary' : 'secondary'"
                    outlined
                    rounded
                    @click="setCommitment(true)"
                >
                    <span>{{ t("commitment") }}</span>
                    <span
                        v-if="getCommitmentExtraTitle"
                        class="text-primary-500 text-sm"
                    >
                        {{ getCommitmentExtraTitle }}
                    </span>
                </BaseButton>
                <BaseButton
                    :label="t('without_commitment')"
                    :severity="!commitment ? 'primary' : 'secondary'"
                    outlined
                    rounded
                    @click="setCommitment(false)"
                />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                <PlanCard
                    v-for="offer in getOffersFormatted"
                    :key="offer.name"
                    :name="offer.name"
                    :description="offer.description"
                    :price="commitment ? offer.price * 12 : offer.price"
                    :discount-price="
                        commitment && offer.commitmentPrice != null
                            ? (offer.commitmentPrice ?? offer.price) * 12
                            : undefined
                    "
                    :price-unit="commitment ? 'year' : 'month'"
                    :price-per-month="
                        commitment
                            ? (offer.commitmentPrice ?? offer.price)
                            : undefined
                    "
                    :features="offer.features ?? []"
                    :selected="offer.selected"
                    :badge="
                        offer.name === OFFER_NAME_MOST_CHOSEN
                            ? t('badge_most_chosen')
                            : undefined
                    "
                    :price-badge="getCommitmentSavingsPill"
                    @click="setOfferSelected(offer.name)"
                />
            </div>

            <div v-if="getModulesFormatted.length" class="w-full">
                <h2
                    class="text-sm font-semibold text-primary-800 mb-1 text-left"
                >
                    {{ t("modules_title") }}
                </h2>
                <p class="text-sm text-primary-600 mb-2">
                    {{ t("modules_help") }}
                </p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <PlanModule
                        v-for="moduleItem in getModulesFormatted"
                        :key="moduleItem.name"
                        :name="moduleItem.name"
                        :display-name="moduleItem.display_name"
                        :price="moduleItem.price"
                        :show-yearly="commitment"
                        :is-selected="moduleSelected?.includes(moduleItem.name)"
                        @update:module-selected="onModuleSelected"
                    />
                </div>
            </div>

            <div class="w-full flex flex-row gap-4 mt-2 justify-between">
                <BaseButton
                    :label="t('previous')"
                    severity="secondary"
                    outlined
                    :disabled="nextLoading"
                    @click="emit('back')"
                />
                <BaseButton
                    :label="t('next')"
                    :disabled="
                        !offerSelected || nextLoading || hasErrorStripeOffers
                    "
                    :loading="nextLoading"
                    @click="emit('next')"
                />
            </div>
        </template>
    </BaseCard>
</template>
